import type { ReactNode, CSSProperties } from 'react';

// ─── Keyframes injected once ─────────────────────────────────────────────────

const STYLE_ID = 'empty-state-kf';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes es-float {
      0%,100% { transform: translateY(0px); }
      50%      { transform: translateY(-7px); }
    }
    @keyframes es-pulse-ring {
      0%   { transform: scale(0.92); opacity: .6; }
      100% { transform: scale(1.18); opacity: 0; }
    }
    @keyframes es-dash {
      to { stroke-dashoffset: 0; }
    }
    @keyframes es-fade-in {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: none; }
    }
    .es-float { animation: es-float 4s ease-in-out infinite; }
    .es-fade  { animation: es-fade-in .4s ease both; }
  `;
  document.head.appendChild(s);
}

// ─── Illustration types ───────────────────────────────────────────────────────

type IllustrationKind =
  | 'server'
  | 'alert'
  | 'cloud'
  | 'code'
  | 'chart'
  | 'search'
  | 'users'
  | 'deploy'
  | 'docker'
  | 'billing'
  | 'ai'
  | 'generic';

function Illustration({ kind }: { kind: IllustrationKind }) {
  const base: CSSProperties = {
    display: 'block',
    margin: '0 auto',
    overflow: 'visible',
  };

  // Shared palette refs
  const accent   = 'var(--accent)';
  const accentS  = 'var(--accent-soft)';
  const accentL  = 'var(--accent-line)';
  const bg2      = 'var(--bg-2)';
  const bg3      = 'var(--bg-3)';
  const line     = 'var(--line-strong)';
  const fg4      = 'var(--fg-4)';
  const ok       = 'var(--ok)';
  const warn     = 'var(--warn)';
  const crit     = 'var(--crit)';

  if (kind === 'server') return (
    <svg style={base} width="96" height="80" viewBox="0 0 96 80" fill="none">
      {/* back server */}
      <rect x="10" y="24" width="76" height="18" rx="5" fill={bg3} stroke={line} strokeWidth="1.2"/>
      <circle cx="22" cy="33" r="3.5" fill={fg4}/>
      <rect x="32" y="30" width="40" height="6" rx="3" fill={fg4} opacity=".4"/>
      {/* front server */}
      <rect x="10" y="46" width="76" height="18" rx="5" fill={bg2} stroke={accentL} strokeWidth="1.4"/>
      <circle cx="22" cy="55" r="3.5" fill={ok}/>
      <rect x="32" y="52" width="40" height="6" rx="3" fill={accentS}/>
      {/* glow dot */}
      <circle cx="22" cy="55" r="6" fill={ok} opacity=".15"/>
      {/* top server */}
      <rect x="18" y="8" width="60" height="14" rx="4" fill={bg3} stroke={line} strokeWidth="1"/>
      <circle cx="28" cy="15" r="2.5" fill={fg4}/>
    </svg>
  );

  if (kind === 'alert') return (
    <svg style={base} width="80" height="84" viewBox="0 0 80 84" fill="none">
      {/* bell body */}
      <path d="M40 14 C26 14 20 26 20 36 L20 52 L12 60 L68 60 L60 52 L60 36 C60 26 54 14 40 14Z" fill={bg3} stroke={line} strokeWidth="1.4"/>
      {/* clapper */}
      <path d="M33 60 C33 64.4 36.1 68 40 68 C43.9 68 47 64.4 47 60" fill="none" stroke={fg4} strokeWidth="1.4"/>
      {/* alert badge */}
      <circle cx="58" cy="20" r="10" fill={bg2} stroke={crit} strokeWidth="1.5"/>
      <text x="58" y="25" textAnchor="middle" fontSize="12" fontWeight="700" fill={crit}>!</text>
      {/* pulse ring */}
      <circle cx="58" cy="20" r="14" fill="none" stroke={crit} strokeWidth="1" opacity=".4"
        style={{ animation: 'es-pulse-ring 1.8s ease-out infinite' }}/>
      {/* top knob */}
      <circle cx="40" cy="14" r="3" fill={bg2} stroke={line} strokeWidth="1.2"/>
    </svg>
  );

  if (kind === 'cloud') return (
    <svg style={base} width="100" height="72" viewBox="0 0 100 72" fill="none">
      {/* cloud shape */}
      <path d="M76 52H28A18 18 0 0 1 28 16 20 20 0 0 1 68 24 16 16 0 0 1 76 52Z" fill={bg3} stroke={line} strokeWidth="1.4"/>
      {/* inner cloud */}
      <path d="M70 52H32A12 12 0 0 1 32 28 14 14 0 0 1 58 34 12 12 0 0 1 70 52Z" fill={bg2} stroke={accentL} strokeWidth="1.2"/>
      {/* plus icon */}
      <line x1="50" y1="37" x2="50" y2="47" stroke={accent} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="45" y1="42" x2="55" y2="42" stroke={accent} strokeWidth="2.5" strokeLinecap="round"/>
      {/* dashed connector */}
      <line x1="50" y1="54" x2="50" y2="65" stroke={fg4} strokeWidth="1" strokeDasharray="3 3"/>
      <rect x="42" y="65" width="16" height="7" rx="2" fill={bg3} stroke={line} strokeWidth="1"/>
    </svg>
  );

  if (kind === 'code') return (
    <svg style={base} width="96" height="76" viewBox="0 0 96 76" fill="none">
      {/* card */}
      <rect x="8" y="8" width="80" height="60" rx="8" fill={bg2} stroke={line} strokeWidth="1.4"/>
      {/* title bar */}
      <rect x="8" y="8" width="80" height="20" rx="8" fill={bg3} stroke={line} strokeWidth="1.4"/>
      <circle cx="22" cy="18" r="3" fill={crit} opacity=".7"/>
      <circle cx="32" cy="18" r="3" fill={warn} opacity=".7"/>
      <circle cx="42" cy="18" r="3" fill={ok} opacity=".7"/>
      {/* code lines */}
      <rect x="18" y="36" width="24" height="4" rx="2" fill={accent} opacity=".6"/>
      <rect x="46" y="36" width="32" height="4" rx="2" fill={fg4}/>
      <rect x="18" y="46" width="16" height="4" rx="2" fill={warn} opacity=".6"/>
      <rect x="38" y="46" width="20" height="4" rx="2" fill={fg4}/>
      <rect x="18" y="56" width="36" height="4" rx="2" fill={fg4}/>
      {/* branch icon */}
      <circle cx="76" cy="22" r="6" fill={accentS} stroke={accentL} strokeWidth="1.2"/>
      <text x="76" y="26.5" textAnchor="middle" fontSize="9" fill={accent} fontWeight="700">⌥</text>
    </svg>
  );

  if (kind === 'chart') return (
    <svg style={base} width="96" height="72" viewBox="0 0 96 72" fill="none">
      {/* axes */}
      <line x1="16" y1="56" x2="82" y2="56" stroke={line} strokeWidth="1.4"/>
      <line x1="16" y1="10" x2="16" y2="56" stroke={line} strokeWidth="1.4"/>
      {/* bars */}
      <rect x="26" y="40" width="12" height="16" rx="3" fill={accentS} stroke={accentL} strokeWidth="1"/>
      <rect x="44" y="28" width="12" height="28" rx="3" fill={accentS} stroke={accentL} strokeWidth="1"/>
      <rect x="62" y="34" width="12" height="22" rx="3" fill={accentS} stroke={accentL} strokeWidth="1"/>
      {/* ghost bars (empty state hint) */}
      <rect x="26" y="22" width="12" height="18" rx="3" fill={bg3} stroke={line} strokeWidth="1" strokeDasharray="3 2"/>
      <rect x="44" y="14" width="12" height="14" rx="3" fill={bg3} stroke={line} strokeWidth="1" strokeDasharray="3 2"/>
      <rect x="62" y="18" width="12" height="16" rx="3" fill={bg3} stroke={line} strokeWidth="1" strokeDasharray="3 2"/>
      {/* question */}
      <circle cx="48" cy="38" r="0" fill="none"/> {/* spacer */}
    </svg>
  );

  if (kind === 'search') return (
    <svg style={base} width="80" height="80" viewBox="0 0 80 80" fill="none">
      <circle cx="34" cy="34" r="20" fill={bg2} stroke={line} strokeWidth="1.8"/>
      <circle cx="34" cy="34" r="13" fill={bg3} stroke={accentL} strokeWidth="1.2"/>
      <line x1="49" y1="49" x2="66" y2="66" stroke={line} strokeWidth="4" strokeLinecap="round"/>
      {/* magnifier lens lines */}
      <line x1="28" y1="34" x2="40" y2="34" stroke={fg4} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="34" y1="28" x2="34" y2="40" stroke={fg4} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );

  if (kind === 'users') return (
    <svg style={base} width="96" height="72" viewBox="0 0 96 72" fill="none">
      {/* back person */}
      <circle cx="32" cy="28" r="11" fill={bg3} stroke={line} strokeWidth="1.4"/>
      <path d="M10 62 C10 50 24 44 32 44 C40 44 54 50 54 62" fill={bg3} stroke={line} strokeWidth="1.4" strokeLinejoin="round"/>
      {/* front person */}
      <circle cx="62" cy="24" r="13" fill={bg2} stroke={accentL} strokeWidth="1.5"/>
      <path d="M36 62 C36 48 50 42 62 42 C74 42 88 48 88 62" fill={bg2} stroke={accentL} strokeWidth="1.5" strokeLinejoin="round"/>
      {/* plus */}
      <circle cx="62" cy="24" r="6" fill={accentS}/>
      <line x1="62" y1="20" x2="62" y2="28" stroke={accent} strokeWidth="2" strokeLinecap="round"/>
      <line x1="58" y1="24" x2="66" y2="24" stroke={accent} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  if (kind === 'deploy') return (
    <svg style={base} width="96" height="80" viewBox="0 0 96 80" fill="none">
      {/* rocket body */}
      <path d="M48 10 C58 10 68 22 68 38 L68 54 L48 64 L28 54 L28 38 C28 22 38 10 48 10Z" fill={bg3} stroke={line} strokeWidth="1.4"/>
      <path d="M48 10 C54 10 60 20 60 34 L60 50 L48 58 L36 50 L36 34 C36 20 42 10 48 10Z" fill={bg2} stroke={accentL} strokeWidth="1.2"/>
      {/* window */}
      <circle cx="48" cy="32" r="7" fill={accentS} stroke={accentL} strokeWidth="1.2"/>
      <circle cx="48" cy="32" r="3.5" fill={accent}/>
      {/* fins */}
      <path d="M28 44 L16 56 L28 54Z" fill={bg3} stroke={line} strokeWidth="1.2"/>
      <path d="M68 44 L80 56 L68 54Z" fill={bg3} stroke={line} strokeWidth="1.2"/>
      {/* exhaust */}
      <path d="M40 64 Q44 72 48 68 Q52 72 56 64" fill="none" stroke={warn} strokeWidth="2" strokeLinecap="round" opacity=".7"/>
    </svg>
  );

  if (kind === 'docker') return (
    <svg style={base} width="96" height="72" viewBox="0 0 96 72" fill="none">
      {/* stack of containers */}
      <rect x="12" y="44" width="72" height="18" rx="5" fill={bg3} stroke={line} strokeWidth="1.4"/>
      <rect x="12" y="28" width="72" height="14" rx="4" fill={bg2} stroke={line} strokeWidth="1.2"/>
      <rect x="12" y="14" width="72" height="12" rx="4" fill={bg3} stroke={line} strokeWidth="1"/>
      {/* grid lines making "whale" container look */}
      <line x1="30" y1="28" x2="30" y2="42" stroke={line} strokeWidth="1"/>
      <line x1="48" y1="28" x2="48" y2="42" stroke={line} strokeWidth="1"/>
      <line x1="66" y1="28" x2="66" y2="42" stroke={line} strokeWidth="1"/>
      {/* accent on middle row */}
      <rect x="14" y="30" width="14" height="10" rx="2" fill={accentS}/>
      <rect x="32" y="30" width="14" height="10" rx="2" fill={bg3}/>
      {/* plus badge */}
      <circle cx="76" cy="14" r="8" fill={bg2} stroke={accentL} strokeWidth="1.2"/>
      <line x1="76" y1="10" x2="76" y2="18" stroke={accent} strokeWidth="2" strokeLinecap="round"/>
      <line x1="72" y1="14" x2="80" y2="14" stroke={accent} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  if (kind === 'billing') return (
    <svg style={base} width="96" height="72" viewBox="0 0 96 72" fill="none">
      <rect x="8" y="12" width="80" height="50" rx="8" fill={bg2} stroke={line} strokeWidth="1.4"/>
      <rect x="8" y="12" width="80" height="16" rx="8" fill={bg3}/>
      <rect x="8" y="20" width="80" height="8" fill={bg3}/>
      {/* stripe */}
      <rect x="8" y="23" width="80" height="5" fill={fg4} opacity=".3"/>
      {/* chip */}
      <rect x="20" y="36" width="20" height="14" rx="3" fill={bg3} stroke={accentL} strokeWidth="1.2"/>
      <line x1="24" y1="36" x2="24" y2="50" stroke={line} strokeWidth="1"/>
      <line x1="30" y1="36" x2="30" y2="50" stroke={line} strokeWidth="1"/>
      <line x1="20" y1="43" x2="40" y2="43" stroke={line} strokeWidth="1"/>
      {/* number dots */}
      <rect x="50" y="38" width="32" height="5" rx="2.5" fill={fg4} opacity=".5"/>
      <rect x="50" y="47" width="20" height="4" rx="2" fill={fg4} opacity=".3"/>
    </svg>
  );

  if (kind === 'ai') return (
    <svg style={base} width="88" height="80" viewBox="0 0 88 80" fill="none">
      {/* head */}
      <rect x="18" y="20" width="52" height="40" rx="12" fill={bg2} stroke={accentL} strokeWidth="1.4"/>
      {/* antenna */}
      <line x1="44" y1="20" x2="44" y2="10" stroke={line} strokeWidth="1.5"/>
      <circle cx="44" cy="8" r="3" fill={accentS} stroke={accentL} strokeWidth="1.2"/>
      {/* eyes */}
      <circle cx="34" cy="34" r="5" fill={accentS} stroke={accentL} strokeWidth="1.2"/>
      <circle cx="34" cy="34" r="2.5" fill={accent}/>
      <circle cx="54" cy="34" r="5" fill={accentS} stroke={accentL} strokeWidth="1.2"/>
      <circle cx="54" cy="34" r="2.5" fill={accent}/>
      {/* mouth */}
      <path d="M32 48 Q44 55 56 48" fill="none" stroke={accentL} strokeWidth="1.8" strokeLinecap="round"/>
      {/* ears */}
      <rect x="10" y="32" width="8" height="14" rx="4" fill={bg3} stroke={line} strokeWidth="1.2"/>
      <rect x="70" y="32" width="8" height="14" rx="4" fill={bg3} stroke={line} strokeWidth="1.2"/>
      {/* thought bubbles */}
      <circle cx="72" cy="16" r="4" fill={bg3} stroke={line} strokeWidth="1"/>
      <circle cx="80" cy="10" r="3" fill={bg3} stroke={line} strokeWidth="1"/>
      <circle cx="85" cy="5" r="2" fill={bg3} stroke={line} strokeWidth="1"/>
    </svg>
  );

  // generic fallback
  return (
    <svg style={base} width="72" height="72" viewBox="0 0 72 72" fill="none">
      <rect x="10" y="10" width="52" height="52" rx="12" fill={bg2} stroke={line} strokeWidth="1.4"/>
      <line x1="24" y1="26" x2="48" y2="26" stroke={accentL} strokeWidth="2" strokeLinecap="round"/>
      <line x1="24" y1="34" x2="40" y2="34" stroke={fg4} strokeWidth="2" strokeLinecap="round"/>
      <line x1="24" y1="42" x2="44" y2="42" stroke={fg4} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function EmptyState({
  kind = 'generic',
  icon,
  title,
  body,
  cta,
  compact = false,
}: {
  kind?: IllustrationKind;
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  cta?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className="es-fade"
      style={{
        padding: compact ? '32px 20px' : '52px 28px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
      }}
    >
      {/* illustration or custom icon */}
      <div className="es-float" style={{ marginBottom: compact ? 16 : 20 }}>
        {icon ?? <Illustration kind={kind} />}
      </div>

      <div style={{
        fontSize: compact ? 14 : 15,
        fontWeight: 600,
        color: 'var(--fg-0)',
        marginBottom: 6,
        letterSpacing: '-0.01em',
      }}>
        {title}
      </div>

      {body && (
        <div style={{
          fontSize: 13,
          color: 'var(--fg-3)',
          lineHeight: 1.6,
          maxWidth: 320,
          marginBottom: cta ? 20 : 0,
        }}>
          {body}
        </div>
      )}

      {cta && <div>{cta}</div>}
    </div>
  );
}
